import ReactGA from "react-ga4";
import ReactPixel from "react-facebook-pixel";
import { trackPurchase as trackTikTokPurchase } from "./tiktokPixel";

export interface TrackPurchaseParams {
  donationId: string;
  amount: string; // Format: "EGP 100" or "USD 50"
  itemId: string; // Program ID or Challenge ID
  itemName: string; // Program title or Challenge name
  itemType?: "program" | "challenge"; // Type of item being purchased
  paymentMethod?: "card" | "cash"; // Payment method used
}

/**
 * Centralized function to track purchase events across all analytics platforms
 * (Google Analytics 4, Facebook Pixel, TikTok Pixel, Google Ads)
 *
 * @param params - Purchase tracking parameters
 */
export const trackPurchaseEvent = (params: TrackPurchaseParams): void => {
  const { donationId, amount, itemId, itemName, itemType = "program", paymentMethod } = params;

  // Extract numeric amount from the amount string (e.g., "EGP 100" -> 100)
  const numericAmount = parseFloat(amount.replace(/[^\d.]/g, ""));

  // Extract currency from the amount string (first 3 characters)
  const currency = amount.substring(0, 3);

  // Only track if we have a valid numeric amount
  if (!numericAmount || isNaN(numericAmount)) {
    // eslint-disable-next-line no-console
    console.warn("[Analytics] Invalid amount provided:", amount);
    return;
  }

  // Track TikTok Pixel purchase
  trackTikTokPurchase(donationId, numericAmount, currency, itemName);

  // Track GA4 Purchase Event - Using correct GA4 event format
  const ga4EventParams: Record<string, any> = {
    currency: currency,
    value: numericAmount,
    transaction_id: donationId,
    // Custom parameters for better tracking
    item_id: itemId,
    item_name: itemName,
    item_type: itemType,
    // Standard GA4 ecommerce items array
    items: [
      {
        item_id: itemId,
        item_name: itemName,
        item_category: itemType,
        price: numericAmount,
        quantity: 1,
      },
    ],
  };

  // Add payment method if provided
  if (paymentMethod) {
    ga4EventParams.payment_method = paymentMethod;
  }

  ReactGA.event("purchase", ga4EventParams);

  // Track Facebook Pixel Purchase - Following best practices
  ReactPixel.track("Purchase", {
    currency: currency,
    value: numericAmount,
    content_ids: [itemId],
    content_name: itemName,
    content_type: "product",
    num_items: 1,
  });

  // Track Google Ads Conversion using gtag
  ReactGA.gtag("event", "conversion", {
    send_to: "AW-1013168892/VVa4CPqwndsZEPz1juMD",
    value: numericAmount,
    currency: currency,
    transaction_id: donationId,
  });
};
